import { createContext, useContext, useEffect, useState } from 'react';

import { getLocationData } from '../../api/mykiss/getLocationData';
import { isBrowser } from '@lib/environment/isBrowser';
import { reportMykissClientError } from '@lib/errors/client';
import { geolocationPermissionGranted } from '@lib/browser/geolocationPermissionGranted';
import { requestLocation } from '@lib/browser/requestLocation';
import { ICloudfrontHeaders, ICoordinates } from '@app-types/geo';

import { getCoordinatesFromLocationData } from './getCoordinatesFromLocationData';

type CoordsSource = 'geolocation_api' | 'ip' | 'default';
type Location = {
  coordinates: ICoordinates;
  countryCode?: string;
  regionCode?: string;
  regionName?: string;
  coordsSource: CoordsSource;
};
type State = { isLoading: boolean; location: Location | undefined };

const GeolocationContext = createContext<State>({
  isLoading: true,
  location: undefined,
});

async function getLocationFromBrowser(): Promise<ICoordinates | null> {
  const isGranted = await geolocationPermissionGranted();
  if (isGranted) {
    return requestLocation();
  }
  return null;
}

const GeolocationProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const [locationData, setLocationData] = useState<ICloudfrontHeaders | null>(null);
  const [{ coordinates, source }, setCoordinates] = useState<{
    coordinates: ICoordinates;
    source: CoordsSource;
  }>(() => getCoordinatesFromLocationData(locationData));
  const [isLoadingBrowserLocation, setIsLoadingBrowserLocation] = useState(true);
  const [isLoadingCDNLocation, setIsLoadingCDNLocation] = useState(true);
  const isLoading = isLoadingBrowserLocation || isLoadingCDNLocation;

  useEffect(() => {
    if (isBrowser()) {
      getLocationData()
        .then(newLocationData => {
          setLocationData(newLocationData);
          if (source !== 'geolocation_api') {
            setCoordinates(getCoordinatesFromLocationData(newLocationData));
          }
          setIsLoadingCDNLocation(false);
        })
        .catch((error: unknown) => {
          setIsLoadingCDNLocation(false);
          reportMykissClientError(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isBrowser()) {
      getLocationFromBrowser()
        .then(location => {
          if (location) {
            setCoordinates({ coordinates: location, source: 'geolocation_api' });
          }
          setIsLoadingBrowserLocation(false);
        })
        .catch((error: unknown) => {
          setIsLoadingBrowserLocation(false);
          reportMykissClientError(error);
        });
    }
  }, []);

  const location = isLoading
    ? undefined
    : {
        coordinates,
        countryCode: locationData?.countryCode ?? undefined,
        regionCode: locationData?.regionCode ?? undefined,
        regionName: locationData?.regionName ?? undefined,
        coordsSource: source,
      };

  return (
    <GeolocationContext.Provider value={{ isLoading, location }}>
      {children}
    </GeolocationContext.Provider>
  );
};

const useGeolocation = (): State => useContext(GeolocationContext);

export { GeolocationProvider, useGeolocation };
